// Voice/video calls from the "Legal Platform Support" team. Only the admin
// places them. Mounted once in the user and lawyer portal layouts (`side` is
// "user" or "lawyer", `api` is userService/lawyerService) so a call can ring
// over any page:
//
//   incoming-support-call (an admin is calling) → ring → Accept → POST acceptSupportCall
//     → support-call-accept → join; Decline → support-call-reject
//   Either side hangs up → support-call-end → the other side gets support-call-ended
//
// Every way a call finishes reports it over REST (POST endSupportCall), which
// writes "Voice call · 02:15" / "Missed video call" into the support chat; the
// open conversation picks it up from the "lwp-support-call-logged" event.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import socketService from "../../services/SocketService";
import { ensureMediaPermission, getMediaPermissionState } from "../call/media";
import MediaPermissionModal from "../call/MediaPermissionModal";
import { useAgora } from "../call/useAgora";
import VideoPlayer from "../call/VideoPlayer";
import { createRinger } from "../lawyer/call/ringer";
import { SUPPORT_NAME, SupportAvatar } from "../../pages/lawyer/SupportConversation";
import "../call/call.css";

// Local fallback for an incoming ring the caller never calls off.
const INCOMING_RING_MS = 60 * 1000;
// How long the other side may be gone from the channel before the call is over.
const LEFT_GRACE_MS = 30 * 1000;

// Lets the lawyer portal's client-call manager turn a client's call away while
// the lawyer is on with support.
let supportCallActive = false;
export const isSupportCallActive = () => supportCallActive;

const sameCall = (a, payload) => !!a && String(payload?.callId) === String(a.callId);

const fmt = (total) => {
  const h = Math.floor(total / 3600);
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return h ? `${h}:${m}:${s}` : `${m}:${s}`;
};

const kindLabel = (type) => (type === "video" ? "video call" : "voice call");

function SupportCallScreen({ session, onHangUp }) {
  const {
    status, error, remoteUsers, localVideoTrack, micOn, camOn, speakerOn,
    toggleMic, toggleCam, toggleSpeaker,
  } = useAgora(session.creds);
  const [seconds, setSeconds] = useState(0);
  const localRef = useRef(null);
  const seenRemoteRef = useRef(false);
  const hangUpRef = useRef(onHangUp);
  hangUpRef.current = onHangUp;
  const isVideo = session.type === "video";

  useEffect(() => {
    if (status !== "connected") return undefined;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  // The other side dropped out of the channel without hanging up.
  const remote = remoteUsers.find((u) => u.videoTrack) || remoteUsers[0];
  useEffect(() => {
    if (remote) {
      seenRemoteRef.current = true;
      return undefined;
    }
    if (!seenRemoteRef.current) return undefined;
    const t = setTimeout(() => hangUpRef.current("left"), LEFT_GRACE_MS);
    return () => clearTimeout(t);
  }, [remote]);

  useEffect(() => {
    const el = localRef.current;
    if (!el || !localVideoTrack || !camOn) return;
    try {
      localVideoTrack.play(el);
    } catch {
      /* not ready yet — retried on next render */
    }
  }, [localVideoTrack, camOn]);

  const remoteVideoTrack = isVideo ? remote?.videoTrack : null;
  const placeholder =
    status === "failed"
      ? error || "Could not start the call."
      : status !== "connected"
      ? "Connecting…"
      : !remote
      ? seenRemoteRef.current
        ? "Support left the call. Waiting for them to reconnect…"
        : "Waiting for support to join…"
      : isVideo
      ? "Support turned their camera off"
      : "Voice call";

  return (
    <div className="lp-call-screen">
      <div className="lp-call-stage">
        <div className="lp-remote">
          {remoteVideoTrack ? (
            <VideoPlayer track={remoteVideoTrack} className="lp-remote-video" />
          ) : (
            <div className="lp-remote-placeholder">
              <SupportAvatar size={120} />
              <div className="lp-ph-text">{placeholder}</div>
            </div>
          )}
        </div>

        <div className="lp-call-top">
          <span className="lp-timer">{fmt(seconds)}</span>
          <span className="lp-enc">
            <span className="lp-enc-dot" /> Encrypted
          </span>
        </div>

        {isVideo && (
          <div className={camOn && localVideoTrack ? "lp-pip" : "lp-pip lp-pip-off"}>
            <div ref={localRef} className="lp-pip-video" />
            <div className="lp-pip-off-label">
              <i className="bi bi-camera-video-off-fill" />
              <span>{camOn ? "Camera unavailable" : "Camera off"}</span>
            </div>
            <span className="lp-pip-dot" />
          </div>
        )}

        <div className="lp-call-info">
          <div className="lp-call-name">{SUPPORT_NAME}</div>
          <div className="lp-call-cats">
            <span className="lp-cat-chip">{isVideo ? "Video call" : "Voice call"}</span>
          </div>
        </div>

        <div className="lp-call-controls">
          <div className="lp-call-ctrl">
            <button className={micOn ? "lp-call-btn" : "lp-call-btn lp-active"} onClick={toggleMic}>
              <i className={micOn ? "bi bi-mic-fill" : "bi bi-mic-mute-fill"} />
            </button>
            <span className="lp-call-ctrl-label">Mute</span>
          </div>
          {isVideo && (
            <div className="lp-call-ctrl">
              <button className={camOn ? "lp-call-btn" : "lp-call-btn lp-active"} onClick={toggleCam}>
                <i className={camOn ? "bi bi-camera-video-fill" : "bi bi-camera-video-off-fill"} />
              </button>
              <span className="lp-call-ctrl-label">Camera</span>
            </div>
          )}
          <div className="lp-call-ctrl">
            <button className={speakerOn ? "lp-call-btn" : "lp-call-btn lp-active"} onClick={toggleSpeaker}>
              <i className={speakerOn ? "bi bi-volume-up-fill" : "bi bi-volume-mute-fill"} />
            </button>
            <span className="lp-call-ctrl-label">Speaker</span>
          </div>
          <div className="lp-call-ctrl">
            <button className="lp-call-btn lp-call-end" onClick={() => onHangUp("hangup")}>
              <i className="bi bi-telephone-x-fill" />
            </button>
            <span className="lp-call-ctrl-label">End Call</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const SupportCallManager = ({ side, api, auth, profile }) => {
  // { callId, type, chat, creds, phase: "incoming"|"call" }
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mediaPrompt, setMediaPrompt] = useState(null); // { reason, state, retry }
  const sessionRef = useRef(null);
  const ringTimer = useRef(null);
  const ringer = useRef(null);
  sessionRef.current = session;
  supportCallActive = !!session;

  if (!ringer.current) ringer.current = createRinger(`${process.env.PUBLIC_URL || ""}/sounds/ringtone.mp3`);

  const stopRinging = useCallback(() => {
    clearTimeout(ringTimer.current);
    ringer.current.stop();
  }, []);

  // Tear down locally and report the end. `reason` is what the server should
  // record for a call nobody answered ("missed" or "rejected").
  const finish = useCallback(
    (reason = "missed", notice = null) => {
      const current = sessionRef.current;
      if (!current) return;
      stopRinging();
      setSession(null);
      setMediaPrompt(null);
      if (notice) toast.info(notice);
      api
        .endSupportCall(current.callId, reason)
        .then((res) => {
          const message = res?.data?.data?.message;
          if (message) {
            window.dispatchEvent(new CustomEvent("lwp-support-call-logged", { detail: message }));
            window.dispatchEvent(new CustomEvent("lwp-support-sent", { detail: message }));
          }
        })
        .catch(() => {});
    },
    [api, stopRinging]
  );

  const hangUp = useCallback(
    (why) => {
      const current = sessionRef.current;
      if (!current) return;
      socketService.emit("support-call-end", { callId: current.callId });
      finish("missed", why === "left" ? "The call was disconnected." : null);
    },
    [finish]
  );

  // Camera/mic before answering.
  const checkMedia = useCallback(async (type, retry) => {
    const video = type === "video";
    const media = await ensureMediaPermission({ video });
    if (media.ok) {
      setMediaPrompt(null);
      return true;
    }
    setMediaPrompt({ reason: media.reason, state: await getMediaPermissionState({ video }), video, retry });
    return false;
  }, []);

  /* ----- incoming ----- */

  const accept = async () => {
    const current = sessionRef.current;
    if (!current || current.phase !== "incoming" || busy) return;
    if (!(await checkMedia(current.type, accept))) return;

    setBusy(true);
    ringer.current.stop();
    try {
      const res = await api.acceptSupportCall(current.callId);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      const d = res.data.data;
      if (!sameCall(sessionRef.current, current)) return;
      socketService.emit("support-call-accept", { callId: current.callId });
      stopRinging();
      setSession({ ...current, creds: { ...d.creds, audioOnly: d.type === "voice" }, phase: "call" });
    } catch (err) {
      finish("missed", err?.response?.data?.message || err?.message || "This call is no longer available.");
    } finally {
      setBusy(false);
    }
  };

  const decline = () => {
    const current = sessionRef.current;
    if (!current) return;
    socketService.emit("support-call-reject", { callId: current.callId });
    finish("rejected");
  };

  /* ----- socket events ----- */

  useEffect(() => {
    if (!profile?.id) return undefined;
    const offs = [
      socketService.subscribe("incoming-support-call", (data) => {
        if (!data?.callId) return;
        // Already on a call (support or a consultation): turn it down.
        if (sessionRef.current || document.querySelector(".lp-call-screen")) {
          if (!sameCall(sessionRef.current, data)) socketService.emit("support-call-reject", { callId: data.callId });
          return;
        }
        const next = { callId: data.callId, type: data.type, chat: data.chat, creds: null, phase: "incoming" };
        setSession(next);
        ringer.current.start();
        clearTimeout(ringTimer.current);
        ringTimer.current = setTimeout(() => {
          if (sameCall(sessionRef.current, next) && sessionRef.current.phase === "incoming") {
            finish("missed", "You missed a call from support.");
          }
        }, INCOMING_RING_MS);
      }),
      // Answered on this account's other device (e.g. the app): just stop ringing.
      socketService.subscribe("support-call-taken", (p) => {
        const current = sessionRef.current;
        if (!sameCall(current, p) || current.phase !== "incoming") return;
        stopRinging();
        setSession(null);
      }),
      socketService.subscribe("support-call-ended", (p) => {
        const current = sessionRef.current;
        if (!sameCall(current, p)) return;
        finish("missed", current.phase === "incoming" ? "You missed a call from support." : "The call has ended.");
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [profile?.id, finish, stopRinging]);

  // Closing the tab mid-call hangs up; the request outlives the page.
  useEffect(() => {
    if (!session) return undefined;
    const onHide = () => {
      const current = sessionRef.current;
      if (!current) return;
      socketService.emit("support-call-end", { callId: current.callId });
      try {
        fetch(`${process.env.REACT_APP_API_URL}${side === "lawyer" ? "/lawyer" : ""}/endSupportCall`, {
          method: "POST",
          keepalive: true,
          headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${auth.getToken()}` },
          body: JSON.stringify({ callId: current.callId, reason: "missed" }),
        });
      } catch {
        /* page is going away regardless */
      }
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [session, side, auth]);

  useEffect(
    () => () => {
      clearTimeout(ringTimer.current);
      ringer.current?.stop();
      supportCallActive = false;
    },
    []
  );

  /* ----- render ----- */

  const permissionModal = mediaPrompt && (
    <MediaPermissionModal
      reason={mediaPrompt.reason}
      state={mediaPrompt.state}
      onRetry={async () => {
        const media = await ensureMediaPermission({ video: mediaPrompt.video });
        if (!media.ok) {
          setMediaPrompt({ ...mediaPrompt, reason: media.reason, state: await getMediaPermissionState({ video: mediaPrompt.video }) });
          return false;
        }
        const retry = mediaPrompt.retry;
        setMediaPrompt(null);
        retry && retry();
        return true;
      }}
      onCancel={() => setMediaPrompt(null)}
    />
  );

  if (session?.phase === "call") {
    return <SupportCallScreen key={session.callId} session={session} onHangUp={hangUp} />;
  }

  if (!session) return permissionModal || null;

  return (
    <div className="lwp lwp-ring-root" role="dialog" aria-modal="true" aria-label="Incoming call">
      <div className="lwp-ring">
        <span className="lwp-ring-kind">
          <i className={`bi ${session.type === "video" ? "bi-camera-video-fill" : "bi-telephone-fill"}`} />{" "}
          Incoming {kindLabel(session.type)}
        </span>

        <div className="lwp-ring-avatar">
          <span className="lwp-ring-pulse" />
          <span className="lwp-ring-pulse delay" />
          <SupportAvatar size={104} />
        </div>
        <h2 className="lwp-ring-name">{SUPPORT_NAME}</h2>
        <p className="lwp-ring-sub">is calling you…</p>

        <div className="lwp-ring-actions">
          <div className="lwp-ring-action">
            <button
              type="button"
              className="lwp-ring-btn decline"
              onClick={decline}
              disabled={busy}
              aria-label="Decline"
            >
              <i className="bi bi-telephone-x-fill" />
            </button>
            <span>Decline</span>
          </div>
          <div className="lwp-ring-action">
            <button type="button" className="lwp-ring-btn accept" onClick={accept} disabled={busy} aria-label="Accept">
              {busy ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-telephone-fill" />}
            </button>
            <span>{busy ? "Connecting…" : "Accept"}</span>
          </div>
        </div>
      </div>
      {permissionModal}
    </div>
  );
};

export default SupportCallManager;
